import type { Drawable, Options } from "roughjs/bin/core";
import type { RoughGenerator } from "roughjs/bin/generator";
import {getDiamondPoints, getArrowheadPoints, getElementAbsoluteCoords} from "../element";
import type { ElementShapes } from "./types";
import type {
  ExcalidrawElement,
  NonDeletedExcalidrawElement,
  ExcalidrawSelectionElement,
  ExcalidrawLinearElement,
  Arrowhead,
  PointBinding,
  ExcalidrawBindableElement,
} from "../element/types";
import {isPathALoop, getCornerRadius, getCornerRadiusByNumber, rotate} from "../math";
import { generateFreeDrawShape } from "../renderer/renderElement";
import { isTransparent, assertNever } from "../utils";
import { simplify } from "points-on-curve";
import { ROUGHNESS, ROUNDNESS } from "../constants";
import {
  isArrowElement,
  isEmbeddableElement,
  isIframeElement,
  isIframeLikeElement,
  isLinearElement,
} from "../element/typeChecks";
import { canChangeRoundness } from "./comparisons";
import {
  geThickArrowPoints,
  getBubblePoints,
  getIsoscelesTrianglePoints,
  getParallelogramPoints,
  getPentagramPoints, arrowheadSize,
} from "../element/bounds";
import { calculateMaxRadius } from "../element/resizeElements";
import Scene from "./Scene";
import { StaticCanvasAppState } from "../types";
import { getFocusPointForConnection } from "../element/binding";
import {isDarkMode} from "../../../../src/utils";
import {DEFAULT_ARROW_COLOR, DEFAULT_DRAW_ST_COLOR} from "../../../../src/Const";

const getDashArrayDashed = (strokeWidth: number) => [8, 8 + strokeWidth];

const getDashArrayDotted = (strokeWidth: number) => [1.5, 6 + strokeWidth];

function adjustRoughness(element: ExcalidrawElement): number {
  const roughness = element.roughness;

  const maxSize = Math.max(element.width, element.height);
  const minSize = Math.min(element.width, element.height);

  // don't reduce roughness if
  if (
      // both sides relatively big
      (minSize >= 20 && maxSize >= 50) ||
      // is round & both sides above 15px
      (minSize >= 15 &&
          !!element.roundness &&
          canChangeRoundness(element.type)) ||
      // relatively long linear element
      (isLinearElement(element) && maxSize >= 50)
  ) {
    return roughness;
  }

  return Math.min(roughness / (maxSize < 10 ? 3 : 2), 2.5);
}

export const generateRoughOptions = (
    element: ExcalidrawElement,
    continuousPath = false,
): Options => {
  let strokeWidth = element.strokeStyle !== "solid"
      ? element.strokeWidth + 0.5
      : element.strokeWidth;

  if (element.strokeStyle == 'none'){
    strokeWidth = 0.00001;
  }
  let szStrokeColor = element.strokeColor;

  const options: Options = {
    seed: element.seed,
    strokeLineDash:
        element.strokeStyle === "dashed"
            ? getDashArrayDashed(element.strokeWidth)
            : element.strokeStyle === "dotted"
                ? getDashArrayDotted(element.strokeWidth)
                : undefined,
    // for non-solid strokes, disable multiStroke because it tends to make
    // dashes/dots overlay each other
    disableMultiStroke: element.strokeStyle !== "solid",
    // for non-solid strokes, increase the width a bit to make it visually
    // similar to solid strokes, because we're also disabling multiStroke
    strokeWidth:strokeWidth,
    // when increasing strokeWidth, we must explicitly set fillWeight and
    // hachureGap because if not specified, roughjs uses strokeWidth to
    // calculate them (and we don't want the fills to be modified)
    fillWeight: element.strokeWidth / 2,
    hachureGap: element.strokeWidth * 4,
    roughness: adjustRoughness(element),
    stroke: szStrokeColor,
    preserveVertices:
        continuousPath || element.roughness < ROUGHNESS.cartoonist,
  };

  switch (element.type) {
    case "rectangle":
    case "roundedRectangle":
    case "ellipse":
    case "triangle":
    case "diamond":
    case "bubble":
    case "parallelogram":
    case "pentagram":
    case "thickArrow":
    case "iframe":
    case "mindmap":
    case "projectLink":
    case "stickyNotes":
    case "embeddable": {
      options.fillStyle = element.fillStyle;
      options.fill = isTransparent(element.backgroundColor) ? undefined : element.backgroundColor;

      if (element.type === "ellipse") {
        options.curveFitting = 1;
      }
      return options;
    }
    case "image":
      return options;
    case "line":
    case "freedraw":
    case "highlighter": {
      if (isPathALoop(element.points)) {
        options.fillStyle = element.fillStyle;
        options.fill =
            element.backgroundColor === "transparent"
                ? undefined
                : element.backgroundColor;
      }
      return options;
    }
    case "arrow":
      return options;
    default: {
      throw new Error(`Unimplemented type ${element.type}`);
    }
  }
};

const modifyIframeLikeForRoughOptions = (
    element: NonDeletedExcalidrawElement,
    isExporting: boolean,
) => {
  if (
      isIframeLikeElement(element) &&
      (isExporting || (isEmbeddableElement(element) && !element.validated)) &&
      isTransparent(element.backgroundColor) &&
      isTransparent(element.strokeColor)
  ) {
    return {
      ...element,
      roughness: 0,
      backgroundColor: "#d3d3d3",
      fillStyle: "solid",
    } as const;
  } else if (isIframeElement(element)) {
    return {
      ...element,
      strokeColor: isTransparent(element.strokeColor)
          ? "#000000"
          : element.strokeColor,
      backgroundColor: isTransparent(element.backgroundColor)
          ? "#f4f4f6"
          : element.backgroundColor,
    };
  }
  return element;
};

const getArrowheadShapes = (
    element: ExcalidrawLinearElement,
    shape: Drawable[],
    position: "start" | "end",
    arrowhead: Arrowhead,
    generator: RoughGenerator,
    options: Options,
    canvasBackgroundColor: string,
) => {
  const arrowheadPoints = getArrowheadPoints(
      element,
      shape,
      position,
      arrowhead,
  );

  if (arrowheadPoints === null) {
    return [];
  }

  switch (arrowhead) {
    case "dot":
    case "circle":
    case "circle_outline": {
      const [x, y, diameter] = arrowheadPoints;

      // always use solid stroke for arrowhead
      delete options.strokeLineDash;

      return [
        generator.circle(x, y, diameter, {
          ...options,
          fill:
              arrowhead === "circle_outline"
                  ? canvasBackgroundColor
                  : element.strokeColor,

          fillStyle: "solid",
          stroke: element.strokeColor,
          roughness: Math.min(0.5, options.roughness || 0),
        }),
      ];
    }
    case "triangle":
    case "triangle_outline": {
      const [x, y, x2, y2, x3, y3] = arrowheadPoints;

      // always use solid stroke for arrowhead
      delete options.strokeLineDash;

      return [
        generator.polygon(
            [
              [x, y],
              [x2, y2],
              [x3, y3],
              [x, y],
            ],
            {
              ...options,
              fill:
                  arrowhead === "triangle_outline"
                      ? canvasBackgroundColor
                      : element.strokeColor,
              fillStyle: "solid",
              roughness: Math.min(1, options.roughness || 0),
            },
        ),
      ];
    }
    case "triangle_concave": {
      const [x2, y2, x3, y3, x4, y4] = arrowheadPoints as any;

      // always use solid stroke for arrowhead
      delete options.strokeLineDash;

      // notch point: move from base-mid towards tip
      const midX = (x3 + x4) / 2;
      const midY = (y3 + y4) / 2;
      // match the menu icon more closely (shallower notch)
      const notchX = midX + (x2 - midX) * 0.4;
      const notchY = midY + (y2 - midY) * 0.4;

      return [
        generator.polygon(
            [
              [x2, y2],
              [x3, y3],
              [notchX, notchY],
              [x4, y4],
              [x2, y2],
            ],
            {
              ...options,
              fill: element.strokeColor,
              fillStyle: "solid",
              // keep arrowheads visually cleaner than the shaft
              roughness: Math.min(0.4, options.roughness || 0),
            },
        ),
      ];
    }
    case "diamond":
    case "diamond_outline": {
      const [x, y, x2, y2, x3, y3, x4, y4] = arrowheadPoints;

      // always use solid stroke for arrowhead
      delete options.strokeLineDash;

      return [
        generator.polygon(
            [
              [x, y],
              [x2, y2],
              [x3, y3],
              [x4, y4],
              [x, y],
            ],
            {
              ...options,
              fill:
                  arrowhead === "diamond_outline"
                      ? canvasBackgroundColor
                      : element.strokeColor,
              fillStyle: "solid",
              roughness: Math.min(1, options.roughness || 0),
            },
        ),
      ];
    }
    case "rectangle":
    case "rectangle_outline": {
      const [rx1, ry1, rx2, ry2, rx3, ry3, rx4, ry4] = arrowheadPoints;

      // always use solid stroke for arrowhead
      delete options.strokeLineDash;

      return [
        generator.polygon(
            [
              [rx1, ry1],
              [rx2, ry2],
              [rx3, ry3],
              [rx4, ry4],
              [rx1, ry1],
            ],
            {
              ...options,
              fill:
                  arrowhead === "rectangle_outline"
                      ? canvasBackgroundColor
                      : element.strokeColor,
              fillStyle: "solid",
              roughness: Math.min(1, options.roughness || 0),
            },
        ),
      ];
    }
    case "bar":
    case "arrow":
    default: {
      const [x2, y2, x3, y3, x4, y4] = arrowheadPoints;

      if (element.strokeStyle === "dotted") {
        // for dotted arrows caps, reduce gap to make it more legible
        const dash = getDashArrayDotted(element.strokeWidth - 1);
        options.strokeLineDash = [dash[0], dash[1] - 1];
      } else {
        // for solid/dashed, keep solid arrow cap
        delete options.strokeLineDash;
      }
      options.roughness = Math.min(1, options.roughness || 0);
      return [
        generator.line(x3, y3, x2, y2, options),
        generator.line(x4, y4, x2, y2, options),
      ];
    }
  }
};

function getConnectionPosition(
    binding: PointBinding | null,
    element: ExcalidrawElement,
    connectionDirection: "startConnectionDirection" | "endConnectionDirection",
) {
  if (binding) {
    const bindingElement = Scene.getScene(element)!.getElement(
        binding.elementId,
    );
    return bindingElement?.[connectionDirection]?.[element.id] || "";
  }
  return "";
}

type Rect = { x1: number; y1: number; x2: number; y2: number };


const getNearestEdgeDirection = (
    bindableElement: ExcalidrawBindableElement,
    pointAbs: readonly [number, number],
): "top" | "right" | "bottom" | "left" => {
  const { x, y, width, height, angle } = bindableElement;
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const [pointerX, pointerY] = rotate(
      pointAbs[0],
      pointAbs[1],
      centerX,
      centerY,
      -angle,
  );

  // If outside bounds, we can directly determine side; otherwise pick nearest edge.
  if (pointerY <= y) {
    return "top";
  }
  if (pointerX >= x + width) {
    return "right";
  }
  if (pointerY >= y + height) {
    return "bottom";
  }
  if (pointerX <= x) {
    return "left";
  }

  const dTop = Math.abs(pointerY - y);
  const dRight = Math.abs(x + width - pointerX);
  const dBottom = Math.abs(y + height - pointerY);
  const dLeft = Math.abs(pointerX - x);
  const min = Math.min(dTop, dRight, dBottom, dLeft);
  if (min === dTop) {
    return "top";
  }
  if (min === dRight) {
    return "right";
  }
  if (min === dBottom) {
    return "bottom";
  }
  return "left";
};

const normalizeRect = (r: Rect): Rect => ({
  x1: Math.min(r.x1, r.x2),
  y1: Math.min(r.y1, r.y2),
  x2: Math.max(r.x1, r.x2),
  y2: Math.max(r.y1, r.y2),
});

const expandRect = (r: Rect, margin: number): Rect => ({
  x1: r.x1 - margin,
  y1: r.y1 - margin,
  x2: r.x2 + margin,
  y2: r.y2 + margin,
});

// axis-aligned intersection for orthogonal segments (elbow routing)
const segmentIntersectsRect = (
    a: readonly [number, number],
    b: readonly [number, number],
    rect: Rect,
    epsilon = 0.0001,
): boolean => {
  const r = normalizeRect(rect);
  const [x1, y1] = a;
  const [x2, y2] = b;

  // only for orthogonal segments
  if (x1 === x2) {
    // vertical
    const x = x1;
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);
    // strict interior intersection (allow touching boundary)
    const xInside = x > r.x1 + epsilon && x < r.x2 - epsilon;
    const yOverlaps = maxY > r.y1 + epsilon && minY < r.y2 - epsilon;
    return xInside && yOverlaps;
  }
  if (y1 === y2) {
    // horizontal
    const y = y1;
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const yInside = y > r.y1 + epsilon && y < r.y2 - epsilon;
    const xOverlaps = maxX > r.x1 + epsilon && minX < r.x2 - epsilon;
    return yInside && xOverlaps;
  }
  // not orthogonal => be conservative
  return true;
};

const polylineIntersectsRects = (
    points: readonly [number, number][],
    rects: readonly Rect[],
): boolean => {
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    for (const rect of rects) {
      if (segmentIntersectsRect(a, b, rect)) {
        return true;
      }
    }
  }
  return false;
};

const dirToVec = (dir: string): readonly [number, number] => {
  switch (dir) {
    case "left":
      return [-1, 0];
    case "right":
      return [1, 0];
    case "top":
      return [0, -1];
    case "bottom":
      return [0, 1];
    default:
      return [1, 0];
  }
};

const dedupeConsecutive = (pts: [number, number][]) => {
  const out: [number, number][] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (!last || last[0] !== p[0] || last[1] !== p[1]) {
      out.push(p);
    }
  }
  return out;
};


// Simplify an orthogonal polyline by removing redundant points:
// - consecutive duplicates (should already be handled by `dedupeConsecutive`)
// - collinear middle points (same x for 3 points, or same y for 3 points)
// - immediate U-turns (A -> B -> A)
const simplifyOrthogonalPolyline = (pts: [number, number][]) => {
  const out: [number, number][] = [];
  for (const p of pts) {
    // collapse immediate U-turns
    if (
        out.length >= 2 &&
        out[out.length - 2][0] === p[0] &&
        out[out.length - 2][1] === p[1]
    ) {
      out.pop();
      continue;
    }

    out.push(p);

    // remove collinear midpoints
    while (out.length >= 3) {
      const a = out[out.length - 3];
      const b = out[out.length - 2];
      const c = out[out.length - 1];
      if (
          (a[0] === b[0] && b[0] === c[0]) ||
          (a[1] === b[1] && b[1] === c[1])
      ) {
        out.splice(out.length - 2, 1);
        continue;
      }
      break;
    }
  }
  return out;
};


const getRoundedOrthogonalPath = (
    pts: readonly [number, number][],
    radius: number,
) => {
  if (pts.length < 2) {
    return "";
  }
  const r0 = Math.max(0, radius);

  const parts: string[] = [];
  const fmt = (n: number) => (Number.isFinite(n) ? n : 0);

  const [sx, sy] = pts[0];
  parts.push(`M ${fmt(sx)} ${fmt(sy)}`);

  let prev: [number, number] = pts[0];

  for (let i = 1; i < pts.length - 1; i++) {
    const curr = pts[i];
    const next = pts[i + 1];

    const [ax, ay] = prev;
    const [bx, by] = curr;
    const [cx, cy] = next;

    const isCorner = !(ax === bx && bx === cx) && !(ay === by && by === cy);
    if (!isCorner || r0 === 0) {
      parts.push(`L ${fmt(bx)} ${fmt(by)}`);
      prev = curr;
      continue;
    }

    // incoming/outgoing vectors are axis-aligned for orthogonal polylines
    const inDx = Math.sign(bx - ax);
    const inDy = Math.sign(by - ay);
    const outDx = Math.sign(cx - bx);
    const outDy = Math.sign(cy - by);

    const inLen = Math.abs(bx - ax) + Math.abs(by - ay);
    const outLen = Math.abs(cx - bx) + Math.abs(cy - by);

    // Clamp radius so we don't overshoot short segments
    const r = Math.min(
        r0,
        Math.max(0, inLen / 2 - 0.01),
        Math.max(0, outLen / 2 - 0.01),
    );

    const p1x = bx - inDx * r;
    const p1y = by - inDy * r;
    const p2x = bx + outDx * r;
    const p2y = by + outDy * r;

    // line to start of corner
    parts.push(`L ${fmt(p1x)} ${fmt(p1y)}`);
    // quadratic curve around the corner (control point at the corner)
    parts.push(`Q ${fmt(bx)} ${fmt(by)} ${fmt(p2x)} ${fmt(p2y)}`);

    prev = [p2x, p2y];
  }

  const [ex, ey] = pts[pts.length - 1];
  parts.push(`L ${fmt(ex)} ${fmt(ey)}`);
  return parts.join(" ");
};


/**
 * Generates the roughjs shape for given element.
 *
 * Low-level. Use `ShapeCache.generateElementShape` instead.
 *
 * @private
 */
export const _generateElementShape = (
    element: Exclude<NonDeletedExcalidrawElement, ExcalidrawSelectionElement>,
    generator: RoughGenerator,
    {
      isExporting,
      canvasBackgroundColor,
    }: { isExporting: boolean; canvasBackgroundColor: string },
    appState?: StaticCanvasAppState,
): Drawable | Drawable[] | null => {
  switch (element.type) {
      /*
      case "rectangle":
      case "roundedRectangle": {
        let shape: ElementShapes[typeof element.type];
        // this is for rendering the stroke/bg of the embeddable, especially
        // when the src url is not set

        if (element.roundness && element.borderRadius) {
          const w = element.width;
          const h = element.height;
          const [topLeft, topRight, bottomRight, bottomLeft] =
              getCornerRadiusByNumber(
                  calculateMaxRadius({
                    width: element.width,
                    height: element.height,
                  }),
                  element.borderRadius,
              )!;

          shape = generator.path(
              `M ${topLeft} 0 L ${
                  w - topRight
              } 0 Q ${w} 0, ${w} ${topRight} L ${w} ${
                  h - bottomRight
              } Q ${w} ${h}, ${
                  w - bottomRight
              } ${h} L ${bottomLeft} ${h} Q 0 ${h}, 0 ${
                  h - bottomLeft
              } L 0 ${topLeft} Q 0 0, ${topLeft} 0`,
              generateRoughOptions(
                  modifyIframeLikeForRoughOptions(element, isExporting),
                  true,
              ),
          );
        } else {
          shape = generator.rectangle(
              0,
              0,
              element.width,
              element.height,
              generateRoughOptions(
                  modifyIframeLikeForRoughOptions(element, isExporting),
                  false,
              ),
          );
        }
        return shape;
      }*/
    case "image": {
      let shape: any;
      // this is for rendering the stroke/bg of the embeddable, especially
      // when the src url is not set
      let strokeWidth = element.strokeWidth;
      if (element.roundness?.value){
        element.borderRadius = [element.roundness?.value,element.roundness?.value,element.roundness?.value,element.roundness?.value]
      }

      if (element.roundness && element.borderRadius) {
        const maxRadius = calculateMaxRadius({
          width: element.width,
          height: element.height,
        });
        let [topLeft, topRight, bottomRight, bottomLeft] =
            getCornerRadiusByNumber(maxRadius, element.borderRadius)!;

        shape = [
          { op: "moveTo", data: [topLeft+strokeWidth, strokeWidth] }, // 1
          { op: "lineTo", data: [element.width - topRight - strokeWidth, strokeWidth] }, //2
          {
            op: "arcTo", //3
            data: [element.width-strokeWidth, strokeWidth, element.width-strokeWidth, topRight+strokeWidth, topRight],
          },
          { op: "lineTo", data: [element.width-strokeWidth, element.height - bottomRight-strokeWidth] }, // 4
          {
            op: "arcTo", //5
            data: [
              element.width-strokeWidth,
              element.height-strokeWidth,
              element.width - bottomRight-strokeWidth,
              element.height-strokeWidth,
              bottomRight,
            ],
          },
          { op: "lineTo", data: [bottomLeft+strokeWidth, element.height-strokeWidth] }, // 6
          {
            op: "arcTo", //5
            data: [
              strokeWidth,
              element.height-strokeWidth,
              strokeWidth,
              element.height-strokeWidth - bottomLeft,
              bottomLeft,
            ],
          },
          { op: "lineTo", data: [strokeWidth, topLeft+strokeWidth] }, // 6
          {
            op: "arcTo", //5
            data: [strokeWidth, strokeWidth, topLeft+strokeWidth, strokeWidth, topLeft],
          },
        ];

        // shape = [
        //   { op: "moveTo", data: [topLeft, 0] }, // 1
        //   { op: "lineTo", data: [element.width - topRight, 0] }, //2
        //   {
        //     op: "arcTo", //3
        //     data: [element.width, 0, element.width, topRight, topRight],
        //   },
        //   { op: "lineTo", data: [element.width, element.height - bottomRight] }, // 4
        //   {
        //     op: "arcTo", //5
        //     data: [
        //       element.width,
        //       element.height,
        //       element.width - bottomRight,
        //       element.height,
        //       bottomRight,
        //     ],
        //   },
        //   { op: "lineTo", data: [bottomLeft, element.height] }, // 6
        //   {
        //     op: "arcTo", //5
        //     data: [
        //       0,
        //       element.height,
        //       0,
        //       element.height - bottomLeft,
        //       bottomLeft,
        //     ],
        //   },
        //   { op: "lineTo", data: [0, topLeft] }, // 6
        //   {
        //     op: "arcTo", //5
        //     data: [0, 0, topLeft, 0, topLeft],
        //   },
        // ];
      } else {
        // shape = [
        //   { op: "moveTo", data: [0, 0] },
        //   { op: "lineTo", data: [element.width, 0] },
        //   { op: "lineTo", data: [element.width, element.height] },
        //   { op: "lineTo", data: [0, element.height] },
        //   { op: "lineTo", data: [0, 0] },
        // ];

        shape = [
          { op: "moveTo", data: [strokeWidth, strokeWidth] },
          { op: "lineTo", data: [element.width-strokeWidth, strokeWidth] },
          { op: "lineTo", data: [element.width-strokeWidth, element.height-strokeWidth] },
          { op: "lineTo", data: [strokeWidth, element.height-strokeWidth] },
          // { op: "lineTo", data: [0, 0] },
        ];
      }
      return shape;
    }
    case "stickyNotes": {
      const shape = generator.rectangle(
          0,
          0,
          0,
          0,
          generateRoughOptions(
              modifyIframeLikeForRoughOptions(element, isExporting),
              false,
          ),
      );
      return shape;
    }
    case "rectangle":
    case "roundedRectangle":
    case "iframe":
    case "mindmap":
    case "image":
    case "projectLink":
    case "embeddable": {
      let shape: ElementShapes[typeof element.type];
      // this is for rendering the stroke/bg of the embeddable, especially
      // when the src url is not set

      if (element.roundness) {
        const w = element.width;
        const h = element.height;
        const r = getCornerRadius(Math.min(w, h), element);
        shape = generator.path(
            `M ${r} 0 L ${w - r} 0 Q ${w} 0, ${w} ${r} L ${w} ${
                h - r
            } Q ${w} ${h}, ${w - r} ${h} L ${r} ${h} Q 0 ${h}, 0 ${
                h - r
            } L 0 ${r} Q 0 0, ${r} 0`,
            generateRoughOptions(
                modifyIframeLikeForRoughOptions(element, isExporting),
                true,
            ),
        );
      } else {
        shape = generator.rectangle(
            0,
            0,
            element.width,
            element.height,
            generateRoughOptions(
                modifyIframeLikeForRoughOptions(element, isExporting),
                false,
            ),
        );
      }
      return shape;
    }
    case "diamond": {
      let shape: ElementShapes[typeof element.type];

      const [topX, topY, rightX, rightY, bottomX, bottomY, leftX, leftY] =
          getDiamondPoints(element);
      if (element.roundness) {
        const verticalRadius = getCornerRadius(Math.abs(topX - leftX), element);

        const horizontalRadius = getCornerRadius(
            Math.abs(rightY - topY),
            element,
        );
        shape = generator.path(
            `M ${topX + verticalRadius} ${topY + horizontalRadius} L ${
                rightX - verticalRadius
            } ${rightY - horizontalRadius}
            C ${rightX} ${rightY}, ${rightX} ${rightY}, ${
                rightX - verticalRadius
            } ${rightY + horizontalRadius}
            L ${bottomX + verticalRadius} ${bottomY - horizontalRadius}
            C ${bottomX} ${bottomY}, ${bottomX} ${bottomY}, ${
                bottomX - verticalRadius
            } ${bottomY - horizontalRadius}
            L ${leftX + verticalRadius} ${leftY + horizontalRadius}
            C ${leftX} ${leftY}, ${leftX} ${leftY}, ${leftX + verticalRadius} ${
                leftY - horizontalRadius
            }
            L ${topX - verticalRadius} ${topY + horizontalRadius}
            C ${topX} ${topY}, ${topX} ${topY}, ${topX + verticalRadius} ${
                topY + horizontalRadius
            }`,
            generateRoughOptions(element, true),
        );
      } else {
        shape = generator.polygon(
            [
              [topX, topY],
              [rightX, rightY],
              [bottomX, bottomY],
              [leftX, leftY],
            ],
            generateRoughOptions(element),
        );
      }
      return shape;
    }
    case "triangle": {
      let shape: ElementShapes[typeof element.type];
      const [topX, topY, rightX, rightY, leftX, leftY] =
          getIsoscelesTrianglePoints(element);
      if (element.roundness) {
        const verticalRadius = getCornerRadius(Math.abs(topX - leftX), element);
        const horizontalRadius = getCornerRadius(
            Math.abs(rightY - topY),
            element,
        );
        shape = generator.path(
            `M ${topX + verticalRadius} ${topY + horizontalRadius} L ${
                rightX - verticalRadius
            } ${rightY - horizontalRadius}
            C ${rightX} ${rightY},${rightX} ${rightY}, ${topX} ${rightY}
            L ${topX} ${leftY}
            C ${leftX} ${leftY}, ${leftX} ${leftY}, ${leftX + verticalRadius} ${
                leftY - horizontalRadius
            } L ${topX - verticalRadius} ${topY + horizontalRadius}
            C ${topX} ${topY}, ${topX} ${topY}, ${topX + verticalRadius} ${
                topY + horizontalRadius
            }`,
            generateRoughOptions(element, true),
        );
      } else {
        shape = generator.polygon(
            [
              [topX, topY],
              [rightX, rightY],
              [leftX, leftY],
            ],
            generateRoughOptions(element),
        );
      }
      return shape;
    }
    case "ellipse": {
      const shape: ElementShapes[typeof element.type] = generator.ellipse(
          element.width / 2,
          element.height / 2,
          element.width,
          element.height,
          generateRoughOptions(element),
      );
      return shape;
    }
    case "bubble": {
      let shape: ElementShapes[typeof element.type];
      const [
        leftTopX,
        leftTopY,
        leftBottomX,
        leftBottomY,
        bottomLeftX,
        bottomLeftY,
        cornerX,
        cornerY,
        bottomCenterX,
        bottomCenterY,
        rightBottomX,
        rightBottomY,
        rightTopX,
        rightTopY,
      ] = getBubblePoints(element);

      if (element.roundness) {
        const w = element.width;
        const h = element.height;
        const r = getCornerRadius(Math.min(w, h), element);

        // const path =
        //   `M ${r} 0 L ${w - r} 0 Q ${w} 0, ${w} ${r}` +
        //   ` L ${w} ${rightBottomY - r} Q ${w} ${rightBottomY}, ${
        //     w - r
        //   } ${rightBottomY}` +
        //   `L ${bottomCenterX + r} ${bottomCenterY}` +
        //   `Q ${bottomCenterX} ${bottomCenterY}, ${cornerX} ${cornerY} ` +
        //   ` L ${cornerX} ${cornerY}` +
        //   `Q ${bottomLeftX} ${bottomLeftY}, ${r} ${bottomLeftY}` +
        //   `L ${r} ${leftBottomY} Q ${0} ${leftBottomY}, ${0} ${
        //     leftBottomY - r
        //   }` +
        //   `L ${0} ${leftTopY + r} Q ${0} ${0}, ${r} ${0}`;

        const path =
            `M ${r} 0 L ${w - r} 0 Q ${w} 0, ${w} ${r}` +
            ` L ${w} ${rightBottomY - r} Q ${w} ${rightBottomY}, ${
                w - r
            } ${rightBottomY}` +
            `L ${bottomCenterX} ${bottomCenterY}` +
            ` L ${cornerX} ${cornerY}` +
            `L ${cornerX} ${leftBottomY} Q ${0} ${leftBottomY}, ${0} ${
                leftBottomY - r
            }` +
            `L ${0} ${leftTopY + r} Q ${0} ${0}, ${r} ${0}`;
        shape = generator.path(path, generateRoughOptions(element));
      } else {
        shape = generator.polygon(
            [
              [leftTopX, leftTopY],
              [leftBottomX, leftBottomY],
              [bottomLeftX, bottomLeftY],
              [cornerX, cornerY],
              [bottomCenterX, bottomCenterY],
              [rightBottomX, rightBottomY],
              [rightTopX, rightTopY],
            ],
            generateRoughOptions(element),
        );
      }
      return shape;
    }
    case "parallelogram": {
      // let shape: ElementShapes[typeof element.type];
      const [
        leftTopX,
        leftTopY,
        rightTopX,
        rightTopY,
        rightBottomX,
        rightBottomY,
        leftBottomX,
        leftBottomY,
      ] = getParallelogramPoints(element);

      const shape: ElementShapes[typeof element.type] = generator.polygon(
          [
            [leftTopX, leftTopY],
            [rightTopX, rightTopY],
            [rightBottomX, rightBottomY],
            [leftBottomX, leftBottomY],
          ],
          generateRoughOptions(element),
      );

      return shape;
    }
    case "pentagram": {
      const [
        topX,
        topY,
        innerRightTopX,
        innerRightTopY,
        rightX,
        rightY,
        innerRightBottomX,
        innerRightBottomY,
        rightBottomX,
        rightBottomY,
        innerBottomX,
        innerBottomY,
        leftBottomX,
        leftBottomY,
        innerLeftBottomX,
        innerLeftBottomY,
        leftX,
        leftY,
        innerLeftTopX,
        innerLeftTopY,
      ] = getPentagramPoints(element);

      const shape: ElementShapes[typeof element.type] = generator.polygon(
          [
            [topX, topY],
            [innerRightTopX, innerRightTopY],
            [rightX, rightY],
            [innerRightBottomX, innerRightBottomY],
            [rightBottomX, rightBottomY],
            [innerBottomX, innerBottomY],
            [leftBottomX, leftBottomY],
            [innerLeftBottomX, innerLeftBottomY],
            [leftX, leftY],
            [innerLeftTopX, innerLeftTopY],
          ],
          generateRoughOptions(element),
      );

      return shape;
    }
    case "thickArrow": {
      const [
        leftTopX,
        leftTopY,
        leftBottomX,
        leftBottomY,
        centerBottomX,
        centerBottomY,
        cornerBottomX,
        cornerBottomY,
        rightX,
        rightY,
        cornerTopX,
        cornerTopY,
        centerTopX,
        centerTopY,
      ] = geThickArrowPoints(element);

      const shape: ElementShapes[typeof element.type] = generator.polygon(
          [
            [leftTopX, leftTopY],
            [leftBottomX, leftBottomY],
            [centerBottomX, centerBottomY],
            [cornerBottomX, cornerBottomY],
            [rightX, rightY],
            [cornerTopX, cornerTopY],
            [centerTopX, centerTopY],
          ],
          generateRoughOptions(element),
      );

      return shape;
    }
    case "line":
    case "arrow": {
      const offset = 2;
      const controlOffset = 100;
      let arrowPath = "";
      let shape: ElementShapes[typeof element.type];
      const options = generateRoughOptions(element);

      // points array can be empty in the beginning, so it is important to add
      // initial position to it
      const points = element.points.length ? element.points : [[0, 0]];

      // curve is always the first element
      // this simplifies finding the curve for an element
      if (!element.roundness) {
        const points1: [number, number][] = [];
        points.forEach((point, i) => {
          points1.push([...point]);
        });

        // const startBindingPosition = getConnectionPosition(
        //     element.startBinding,
        //     element,
        //     "startConnectionDirection",
        // );
        // if (startBindingPosition == 'right'){
        //   points1[0][0] = points[0][0] + 1;
        //   points1[1][0] = points[1][0] - 2;
        // }else if (startBindingPosition == 'left'){
        //   points1[0][0] = points[0][0] - 1.8;
        //   points1[1][0] = points[1][0] + 2;
        // }else if (startBindingPosition == 'top'){
        //   points1[1][1] = points[1][1] + 2;
        //   points1[0][1] = points[0][1] - 1.8;
        // }else if (startBindingPosition == 'bottom'){
        //   points1[1][1] = points[1][1] - 3;
        //   points1[0][1] = points[0][1] + 1;
        // }

        if (options.fill) {
          shape = [generator.polygon(points1 as [number, number][], options)];
        } else {
          shape = [generator.linearPath(points1 as [number, number][], options)];
        }
      } else if (element.roundness.type === ROUNDNESS.ELBOW) {
        //region elbow
        const cornerRadius = 6;
        if (points.length === 2) {
          const startBindingPosition = getConnectionPosition(
              element.startBinding,
              element,
              "startConnectionDirection",
          );
          let endBindingPosition = getConnectionPosition(
              element.endBinding,
              element,
              "endConnectionDirection",
          );
          // While dragging to create/bind an elbow connector, `endBinding` is not
          // yet committed, but we still want the preview arrowhead to be
          // perpendicular to the hovered edge. Use suggested binding direction
          // (same approach as for curved connectors below).
          let suggestedEndBindingElement:
              | ExcalidrawBindableElement
              | undefined = undefined;
          if (!endBindingPosition) {
            suggestedEndBindingElement = appState?.suggestedBindings?.[0] as
                | ExcalidrawBindableElement
                | undefined;
            if (suggestedEndBindingElement) {
              const { currentDirection } = getFocusPointForConnection(
                  suggestedEndBindingElement,
                  element,
                  "end",
              );
              endBindingPosition = currentDirection;
            }
          }

          const startPoint = points[0];
          const endPoint = points[1];

          // Determine preferred directions (if bound, keep the stored side)
          const dx = endPoint[0] - startPoint[0];
          const dy = endPoint[1] - startPoint[1];
          const inferDir = (isStart: boolean) => {
            if (isStart && startBindingPosition) {
              return startBindingPosition;
            }
            if (!isStart && endBindingPosition) {
              return endBindingPosition;
            }
            // fallback: choose based on dominant axis
            if (Math.abs(dx) >= Math.abs(dy)) {
              if (isStart) {
                return dx >= 0 ? "right" : "left";
              }
              return dx >= 0 ? "left" : "right";
            }
            if (isStart) {
              return dy >= 0 ? "bottom" : "top";
            }
            return dy >= 0 ? "top" : "bottom";
          };

          const startDir = inferDir(true);
          const endDir = inferDir(false);

          const rightOffset = 0;

          // Build obstacle rects in element-local coords (subtract linear element origin)
          const rects: Rect[] = [];
          const scene = Scene.getScene(element);
          const addRectForBinding = (binding: PointBinding | null) => {
            if (!binding || !scene) {
              return;
            }
            const be = scene.getElement(
                binding.elementId,
            ) as ExcalidrawElement | null;
            if (!be) {
              return;
            }
            const [x1, y1, x2, y2] = getElementAbsoluteCoords(be);
            rects.push({
              x1: x1 - element.x,
              y1: y1 - element.y,
              x2: x2 - element.x - rightOffset,
              y2: y2 - element.y,
            });
          };
          addRectForBinding(element.startBinding);
          addRectForBinding(element.endBinding);
          // During drag preview `endBinding` can be null, so we must still
          // treat the hovered element as an obstacle to avoid routing through it.
          if (!element.endBinding && suggestedEndBindingElement) {
            const [x1, y1, x2, y2] = getElementAbsoluteCoords(
                suggestedEndBindingElement,
            );
            rects.push({
              x1: x1 - element.x,
              y1: y1 - element.y,
              x2: x2 - element.x - rightOffset,
              y2: y2 - element.y,
            });
          }

          const margin = 6;
          const obstacles = rects.map((r) => expandRect(r, margin));

          // Add small "stubs" leaving each widget to reduce self-intersections.
          // When widgets are very close, a large fixed stub causes awkward extra
          // bends near the end widget. Use a distance-aware stub length.
          const manhattan = Math.abs(dx) + Math.abs(dy);
          const STUB = Math.max(10, Math.min(24, Math.round(manhattan / 4)));
          const [svx, svy] = dirToVec(startDir);
          const [evx, evy] = dirToVec(endDir);
          const startStub: [number, number] = [
            startPoint[0] + svx * STUB,
            startPoint[1] + svy * STUB,
          ];
          const endStub: [number, number] = [
            endPoint[0] + evx * STUB,
            endPoint[1] + evy * STUB,
          ];

          const buildHVH = (a: [number, number], b: [number, number]) => {
            const midX = (a[0] + b[0]) / 2;
            return dedupeConsecutive([a, [midX, a[1]], [midX, b[1]], b]);
          };
          const buildVHV = (a: [number, number], b: [number, number]) => {
            const midY = (a[1] + b[1]) / 2;
            return dedupeConsecutive([a, [a[0], midY], [b[0], midY], b]);
          };
          const routeViaY = (
              a: [number, number],
              b: [number, number],
              y: number,
          ) => {
            return dedupeConsecutive([a, [a[0], y], [b[0], y], b]);
          };
          const routeViaX = (
              a: [number, number],
              b: [number, number],
              x: number,
          ) => {
            return dedupeConsecutive([a, [x, a[1]], [x, b[1]], b]);
          };

          const baseA: [number, number] = startStub;
          const baseB: [number, number] = endStub;

          const candidates: [number, number][][] = [];

          // Prefer simplest paths first:
          // - straight (if aligned)
          // - single-bend (HV / VH)
          // then fall back to 3-bend (HVH / VHV) and detours if needed.
          //
          // We also prefer the single-bend that continues in the start direction
          // first (horizontal vs vertical).
          const buildHV = (a: [number, number], b: [number, number]) =>
              dedupeConsecutive([a, [b[0], a[1]], b]);
          const buildVH = (a: [number, number], b: [number, number]) =>
              dedupeConsecutive([a, [a[0], b[1]], b]);

          if (baseA[0] === baseB[0] || baseA[1] === baseB[1]) {
            candidates.push(dedupeConsecutive([baseA, baseB]));
          }

          if (startDir === "left" || startDir === "right") {
            candidates.push(buildHV(baseA, baseB));
            candidates.push(buildVH(baseA, baseB));
          } else {
            candidates.push(buildVH(baseA, baseB));
            candidates.push(buildHV(baseA, baseB));
          }

          // Prefer respecting start direction: if start is horizontal, try HVH first, else VHV
          if (startDir === "left" || startDir === "right") {
            candidates.push(buildHVH(baseA, baseB));
            candidates.push(buildVHV(baseA, baseB));
          } else {
            candidates.push(buildVHV(baseA, baseB));
            candidates.push(buildHVH(baseA, baseB));
          }

          // Detours outside both boxes
          if (obstacles.length) {
            const topMost = Math.min(...obstacles.map((r) => r.y1));
            const bottomMost = Math.max(...obstacles.map((r) => r.y2));
            const leftMost = Math.min(...obstacles.map((r) => r.x1));
            const rightMost = Math.max(...obstacles.map((r) => r.x2));
            candidates.push(routeViaY(baseA, baseB, topMost - STUB));
            candidates.push(routeViaY(baseA, baseB, bottomMost + STUB));
            candidates.push(routeViaX(baseA, baseB, leftMost - STUB));
            candidates.push(routeViaX(baseA, baseB, rightMost + STUB));
          }

          // Choose the best non-intersecting route. Previously we picked the
          // first matching candidate, which can place the last bend too close
          // to the end widget (especially for single-bend HV/VH routes).
          // Here we score candidates by preferring bends closer to the middle
          // of the polyline (between endpoints) while keeping the path short.
          const scoreRoute = (poly: [number, number][]) => {
            // Use the same simplification we apply to the final polyline so the
            // score matches what users actually see.
            const full = simplifyOrthogonalPolyline(
                dedupeConsecutive([
                  startPoint as [number, number],
                  ...poly,
                  endPoint as [number, number],
                ]),
            );
            let total = 0;
            for (let i = 0; i < full.length - 1; i++) {
              total += Math.abs(full[i + 1][0] - full[i][0]);
              total += Math.abs(full[i + 1][1] - full[i][1]);
            }
            if (total <= 0) {
              return 0;
            }

            const centerX = (startPoint[0] + endPoint[0]) / 2;
            const centerY = (startPoint[1] + endPoint[1]) / 2;

            // Prefer bends to be at least 25% away from either end along the path.
            const desired = Math.min(total * 0.25, Math.max(0, total / 2 - 1));
            let penalty = 0;
            let distFromStart = 0;
            for (let i = 1; i < full.length - 1; i++) {
              // advance by previous segment
              distFromStart +=
                  Math.abs(full[i][0] - full[i - 1][0]) +
                  Math.abs(full[i][1] - full[i - 1][1]);
              const minEdgeDist = Math.min(
                  distFromStart,
                  total - distFromStart,
              );
              if (minEdgeDist < desired) {
                penalty += (desired - minEdgeDist) * 10;
              }

              // Additional preference: bends should be closer to the midpoint
              // between the two widgets (helps for short distances).
              const prev = full[i - 1];
              const curr = full[i];
              const next = full[i + 1];
              const isBend = !(
                  (prev[0] === curr[0] && curr[0] === next[0]) ||
                  (prev[1] === curr[1] && curr[1] === next[1])
              );
              if (isBend) {
                const manhattanToCenter =
                    Math.abs(curr[0] - centerX) + Math.abs(curr[1] - centerY);
                // keep this weight small so obstacles/shortness still win
                penalty += manhattanToCenter * 0.5;
              }
            }

            // Small penalty for extra bends (keeps detours reasonable).
            const bends = Math.max(0, full.length - 2);
            penalty += bends * 2;

            return total + penalty;
          };

          // pick the first non-intersecting inner route
          let inner = candidates[0];
          let bestScore = Number.POSITIVE_INFINITY;
          for (const cand of candidates) {
            if (polylineIntersectsRects(cand, obstacles)) {
              continue;
            }
            const score = scoreRoute(cand);
            if (score < bestScore) {
              bestScore = score;
              inner = cand;
            }
          }

          // `inner` is routed between `startStub` and `endStub` (baseA/baseB),
          // so we only need to prepend/append the actual endpoints.
          const elbowPoints = simplifyOrthogonalPolyline(
              dedupeConsecutive([
                startPoint as [number, number],
                ...inner,
                endPoint as [number, number],
              ]),
          );

          const elbowPoints1: [number, number][] = [];
          elbowPoints.forEach((point, i) => {
            elbowPoints1.push([...point]);
          });
          if (elbowPoints1.length >= 1){
            // const nLastIndex = elbowPoints1.length -1;
            // if (startDir == 'bottom'){
            //   elbowPoints1[nLastIndex][1] = elbowPoints[nLastIndex][1] - 3;
            //   elbowPoints1[0][1] = elbowPoints[0][1] + 1;
            // }else if (startDir == 'right'){
            //   elbowPoints1[0][0] = elbowPoints[0][0] + 1;
            //   elbowPoints1[nLastIndex][0] = elbowPoints[nLastIndex][0] - 3;
            // }else if (startDir == 'top'){
            //   elbowPoints1[nLastIndex][1] = elbowPoints[nLastIndex][1] + 2;
            //   elbowPoints1[0][1] = elbowPoints[0][1] - 1;
            // }else if (startDir == 'left'){
            //   elbowPoints1[0][0] = elbowPoints[0][0] - 1;
            //   elbowPoints1[nLastIndex][0] = elbowPoints[nLastIndex][0] + 2;
            // }
          }

          const roundedPath = getRoundedOrthogonalPath(
              elbowPoints1,
              cornerRadius,
          );
          shape = roundedPath
              ? [generator.path(roundedPath, options)]
              : [generator.linearPath(elbowPoints1, options)];
        } else {
          // Multi-point line: stick to simple orthogonal routing between user points
          const elbowPoints: [number, number][] = [];
          points.forEach((point, i) => {
            elbowPoints.push(point as [number, number]);
            if (i < points.length - 1) {
              const nextPoint = points[i + 1];
              if (point[0] !== nextPoint[0] && point[1] !== nextPoint[1]) {
                elbowPoints.push([nextPoint[0], point[1]]);
              }
            }
          });
          const simplified = simplifyOrthogonalPolyline(
              dedupeConsecutive(elbowPoints),
          );
          const roundedPath = getRoundedOrthogonalPath(
              simplified,
              cornerRadius,
          );
          shape = roundedPath
              ? [generator.path(roundedPath, options)]
              : [generator.linearPath(simplified, options)];
        }
        //endregion elbow----
      } else if (points.length < 3) {
        // Defensive: malformed lines/arrows may have <2 points (e.g. legacy/corrupt
        // data). Avoid crashing the renderer in our custom curved-connector path.
        if (!points[0] || !points[1]) {
          shape = [generator.linearPath(points as any, options)];
          break;
        }
        const { startBinding, endBinding } = element;
        const startBindingPosition = getConnectionPosition(
            startBinding,
            element,
            "startConnectionDirection",
        );

        let endBindingPosition = getConnectionPosition(
            endBinding,
            element,
            "endConnectionDirection",
        );

        const suggestedBindingElement = appState?.suggestedBindings?.[0] as
            | ExcalidrawBindableElement
            | undefined;
        // If suggested bindings are filtered out (e.g. dragging along different
        // edges of the same already-bound widget), fall back to the bound end
        // element to compute the nearest edge direction for preview.
        const scene = Scene.getScene(element);
        const boundEndElement = endBinding
            ? (scene?.getElement(
                endBinding.elementId,
            ) as ExcalidrawBindableElement | null)
            : null;
        const targetForPreview = suggestedBindingElement || boundEndElement;
        if (targetForPreview) {
          const endPointAbs: [number, number] = [
            element.x + points[points.length - 1][0],
            element.y + points[points.length - 1][1],
          ];
          endBindingPosition = getNearestEdgeDirection(
              targetForPreview,
              endPointAbs,
          );
        }
        let path = "M";
        let leftOrRight = "left";
        if (points[1]) {
          leftOrRight = points[1][0] - points[0][0] > 0 ? "right" : "left";
        }

        const points1: [number, number][] = [];
        points.forEach((point, i) => {
          points1.push([...point]);
        });
        if (points1.length > 1){
          if (startBindingPosition == 'right'){
            points1[0][0] = points[0][0] + 0;
            points1[1][0] = points[1][0] + 1;
          }else if (startBindingPosition == 'left'){
            points1[0][0] = points[0][0] + 1.6;
            points1[1][0] = points[1][0] - 2;
          }else if (startBindingPosition == 'top'){
            points1[1][1] = points[1][1] - 1.8;
            points1[0][1] = points[0][1] + 0.8;
          }else if (startBindingPosition == 'bottom'){
            points1[1][1] = points[1][1] + 1;
            points1[0][1] = points[0][1] - 1;
          }
        }


        points1.forEach((point, index) => {
          let [x, y] = point;
          // 起点
          if (index === 0) {
            x -= 1;
            switch (startBindingPosition) {
              case "top":
                path += ` ${x} ${y - offset}`;
                break;
              case "right":
                path += ` ${x + offset} ${y}`;
                break;
              case "bottom":
                path += ` ${x} ${y + offset}`;
                break;
              case "left":
                path += ` ${x - offset} ${y}`;
                break;
              default:
                path += ` ${x} ${y}`;
            }
          }

          const nextPoint = points1[index + 1];
          if (nextPoint) {
            const deltaX = Math.abs(nextPoint[0] - x);
            const deltaY = Math.abs(nextPoint[1] - y);

            const startControlPoint = [];
            const endControlPoint = [];

            switch (startBindingPosition) {
              case "top":
                startControlPoint[0] = x;
                startControlPoint[1] = -Math.max(deltaY / 2, controlOffset);
                break;
              case "right":
                startControlPoint[0] = Math.max(deltaX / 2, controlOffset);
                startControlPoint[1] = y;
                break;
              case "bottom":
                startControlPoint[0] = x;
                startControlPoint[1] = Math.max(deltaY / 2, controlOffset);
                break;
              case "left":
                startControlPoint[0] = -Math.max(deltaX / 2, controlOffset);
                startControlPoint[1] = y;
                break;
              default:
                startControlPoint[0] = Math.max(deltaX / 2, controlOffset);
                startControlPoint[1] = y;
            }

            switch (endBindingPosition) {
              case "top":
                endControlPoint[0] = nextPoint[0];
                endControlPoint[1] =
                    nextPoint[1] - Math.max(deltaY / 2, controlOffset);
                break;
              case "right":
                endControlPoint[0] =
                    nextPoint[0] + Math.max(deltaX / 2, controlOffset);
                endControlPoint[1] = nextPoint[1];
                break;
              case "bottom":
                endControlPoint[0] = nextPoint[0];
                endControlPoint[1] =
                    nextPoint[1] + Math.max(deltaY / 2, controlOffset);
                break;
              case "left":
                endControlPoint[0] =
                    nextPoint[0] - Math.max(deltaX / 2, controlOffset);
                endControlPoint[1] = nextPoint[1];
                break;
              default:
                if (startBindingPosition === "left") {
                  endControlPoint[0] =
                      nextPoint[0] + Math.max(deltaX / 2, controlOffset);
                  endControlPoint[1] = nextPoint[1];
                } else if (leftOrRight === "left") {
                  endControlPoint[0] =
                      nextPoint[0] + Math.max(deltaX / 2, controlOffset);
                  endControlPoint[1] = nextPoint[1];
                } else if (leftOrRight === "right") {
                  endControlPoint[0] =
                      nextPoint[0] - Math.max(deltaX / 2, controlOffset);
                  endControlPoint[1] = nextPoint[1];
                }
            }

            // 起始控制点
            path += ` C ${startControlPoint[0]} ${startControlPoint[1]},`;
            // 中止控制点
            path += ` ${endControlPoint[0]} ${endControlPoint[1]},`;
            arrowPath = path;
            // When the end arrowhead is null (no arrowhead / 横线), use a small
            // offset identical to the start point so both ends look consistent.
            // Only pull back by arrowheadSize when there IS an arrowhead to draw.
            const hasEndArrowhead = element.endArrowhead !== null;
            const endPullback = hasEndArrowhead ? arrowheadSize - 1 : offset + 1;
            switch (endBindingPosition) {
              case "top":
                path += ` ${nextPoint[0]} ${nextPoint[1] - endPullback}`;
                arrowPath += ` ${nextPoint[0]} ${nextPoint[1] - offset * 2}`;
                break;
              case "right":
                path += ` ${nextPoint[0] + endPullback} ${nextPoint[1]}`;
                arrowPath += ` ${nextPoint[0] + offset * 2} ${nextPoint[1]}`;
                break;
              case "bottom":
                path += ` ${nextPoint[0]} ${nextPoint[1] + endPullback}`;
                arrowPath += ` ${nextPoint[0]} ${nextPoint[1] + offset * 2}`;
                break;
              case "left":
                if (startBindingPosition === "right") {
                  path += ` ${nextPoint[0] - endPullback} ${
                      nextPoint[1]
                  }`;
                  arrowPath += ` ${nextPoint[0] - offset * 2} ${nextPoint[1]}`;
                } else {
                  path += ` ${nextPoint[0] - (hasEndArrowhead ? 2 * 2 - 2 : offset + 1)} ${nextPoint[1]}`;
                  arrowPath += ` ${nextPoint[0] - offset * 2 + 2} ${
                      nextPoint[1]
                  }`;
                }
                break;
              default:
                path += ` ${nextPoint[0]} ${nextPoint[1]}`;
                arrowPath += ` ${nextPoint[0]} ${nextPoint[1]}`;
            }
          }
        });

        shape = [generator.path(path, options)];
      } else {
        shape = [generator.curve(points as [number, number][], options)];
      }

      // add lines only in arrow
      if (element.type === "arrow") {
        const { startArrowhead = null, endArrowhead = "arrow" } = element;

        if (startArrowhead !== null) {
          const shapes = getArrowheadShapes(
              element,
              arrowPath ? [generator.path(arrowPath, options)] : shape,
              "start",
              startArrowhead,
              generator,
              options,
              canvasBackgroundColor,
          );
          shape.push(...shapes);
        }

        if (endArrowhead !== null) {
          if (endArrowhead === undefined) {
            // Hey, we have an old arrow here!
          }

          const shapes = getArrowheadShapes(
              element,
              arrowPath ? [generator.path(arrowPath, options)] : shape,
              "end",
              endArrowhead,
              generator,
              options,
              canvasBackgroundColor,
          );
          shape.push(...shapes);
        }
      }
      return shape;
    }
    case "freedraw":
    case "highlighter": {
      let shape: ElementShapes[typeof element.type];
      generateFreeDrawShape(element);

      if (isPathALoop(element.points)) {
        // generate rough polygon to fill freedraw shape
        const simplifiedPoints = simplify(element.points, 0.75);
        shape = generator.curve(simplifiedPoints as [number, number][], {
          ...generateRoughOptions(element),
          stroke: "none",
        });
      } else {
        shape = null;
      }
      return shape;
    }
    case "frame":
    case "magicframe":
    case "text":
    case "image": {
      const shape: ElementShapes[typeof element.type] = null;
      // we return (and cache) `null` to make sure we don't regenerate
      // `element.canvas` on rerenders
      return shape;
    }
    default: {
      assertNever(
          element,
          `generateElementShape(): Unimplemented type ${(element as any)?.type}`,
      );
      return null;
    }
  }
};
